import React, { useMemo, useState } from "react";

// Campi usati per identificare una riga a colpo d'occhio quando non si
// raggruppa per articolo (vedi sotto) — i primi che trovano un valore non
// vuoto in quella riga, escluso il campo che si sta compilando.
const CAMPI_CONTESTO = ["Riga_Articolo", "Riga_Combinazione", "Riga_Variante", "Taglia_Nome", "Testata_NumOrdCli"];

function etichettaRiga(riga, campo, indice) {
  const testo = CAMPI_CONTESTO
    .filter((c) => c !== campo)
    .map((c) => riga[c])
    .filter((v) => String(v ?? "").trim() !== "")
    .join(" · ");
  return testo || `riga ${indice + 1}`;
}

// Popup "⚠️ N mancanti" (vedi OrdiniMapping.jsx, intestazione colonna): un
// campo obbligatorio per l'API del gestionale ha ancora celle vuote — qui
// si vedono e si compilano TUTTE insieme, invece di scorrere l'anteprima
// cercando le caselle evidenziate una per una. Stessa idea di
// AssegnaSviluppoModal.jsx ("uno sviluppo diverso per articolo"): quando
// Riga_Articolo è disponibile (e non è lui il campo da compilare) si
// raggruppa per articolo — lo stesso valore vale quasi sempre per tutte le
// taglie dello stesso articolo — altrimenti una riga alla volta. "Applica"
// scrive SOLO sulle righe del gruppo ancora vuote per questo campo, mai su
// una già compilata diversamente.
export default function CampiMancantiModal({ campo, indiciMancanti, righe, raggruppaPerArticolo, onScrivi, onChiudi }) {
  const [modo, setModo] = useState(raggruppaPerArticolo ? "perGruppo" : "perRiga");
  const [valoreUnico, setValoreUnico] = useState("");
  const [perGruppo, setPerGruppo] = useState({}); // { chiaveGruppo: valore }

  const gruppi = useMemo(() => {
    const mappa = new Map();
    for (const i of indiciMancanti) {
      const riga = righe[i];
      const chiave = raggruppaPerArticolo ? String(riga.Riga_Articolo ?? `__riga_${i}`) : `__riga_${i}`;
      if (!mappa.has(chiave)) {
        mappa.set(chiave, {
          chiave,
          etichetta: raggruppaPerArticolo ? (riga.Riga_Articolo || "(senza articolo)") : etichettaRiga(riga, campo, i),
          indici: [],
        });
      }
      mappa.get(chiave).indici.push(i);
    }
    return [...mappa.values()];
  }, [indiciMancanti, righe, raggruppaPerArticolo, campo]);

  const gruppiCompilati = gruppi.filter((g) => (perGruppo[g.chiave] || "").trim());

  function applicaTutte() {
    const valore = valoreUnico.trim();
    if (!valore) return;
    for (const i of indiciMancanti) onScrivi(i, valore);
    setValoreUnico("");
  }

  function applicaPerGruppo() {
    if (gruppiCompilati.length === 0) return;
    for (const g of gruppiCompilati) {
      const valore = perGruppo[g.chiave].trim();
      for (const i of g.indici) onScrivi(i, valore);
    }
    setPerGruppo((prev) => {
      const copia = { ...prev };
      for (const g of gruppiCompilati) delete copia[g.chiave];
      return copia;
    });
  }

  return (
    <div
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "Inter, sans-serif" }}
      onClick={onChiudi}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: "var(--cf-bg-card)", borderRadius: "16px", width: "640px", maxWidth: "92vw", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 25px 60px rgba(0,0,0,0.45)" }}
      >
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #7f1d1d 100%)", padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px", lineHeight: 1 }}>⚠️</span>
            <div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontWeight: 700, fontSize: "15px", letterSpacing: "1px", textTransform: "uppercase", color: "#f1f5f9" }}>
                Campo obbligatorio mancante
              </div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "11px", color: "var(--cf-text-faint)", letterSpacing: "0.3px", marginTop: "3px" }}>
                {campo}
              </div>
            </div>
          </div>
          <button className="cf-btn" onClick={onChiudi} style={{ border: "none", background: "rgba(255,255,255,0.08)", borderRadius: "6px", width: "28px", height: "28px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", color: "#cbd5e1", lineHeight: 1 }}>
            ✕
          </button>
        </div>

        <div style={{ padding: "24px", overflowY: "auto" }}>
          {indiciMancanti.length === 0 ? (
            <p className="testo-secondario" style={{ marginTop: 0 }}>
              ✅ Nessuna riga senza questo campo — tutte compilate.
            </p>
          ) : (
            <>
              <div style={{ display: "flex", gap: "8px", marginBottom: "18px" }}>
                <button
                  onClick={() => setModo("tutte")}
                  style={modo === "tutte" ? { backgroundColor: "#7f1d1d", color: "#fff", borderColor: "#7f1d1d" } : undefined}
                >
                  Stesso valore per tutte
                </button>
                <button
                  onClick={() => setModo("perGruppo")}
                  style={modo === "perGruppo" ? { backgroundColor: "#7f1d1d", color: "#fff", borderColor: "#7f1d1d" } : undefined}
                >
                  {raggruppaPerArticolo ? `Un valore diverso per articolo (${gruppi.length})` : `Un valore diverso per riga (${gruppi.length})`}
                </button>
              </div>

              {modo === "tutte" ? (
                <>
                  <p className="testo-secondario" style={{ marginTop: 0 }}>
                    Scrive lo stesso valore su tutte le {indiciMancanti.length} righe ancora vuote per "{campo}".
                  </p>
                  <div className="riga-controlli">
                    <input
                      type="text"
                      placeholder="valore..."
                      value={valoreUnico}
                      onChange={(e) => setValoreUnico(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") applicaTutte(); }}
                      style={{ minWidth: "14rem" }}
                    />
                    <button className="btn-primario" onClick={applicaTutte} disabled={!valoreUnico.trim()}>
                      Applica a tutte ({indiciMancanti.length})
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="testo-secondario" style={{ marginTop: 0 }}>
                    {raggruppaPerArticolo
                      ? "Un valore per ciascun articolo — si applica a tutte le righe di quell'articolo ancora vuote per questo campo."
                      : "Un valore per ciascuna riga."}{" "}
                    Lascia vuoti quelli che non vuoi toccare adesso.
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "14px", maxHeight: "360px", overflowY: "auto" }}>
                    {gruppi.map((g) => (
                      <div key={g.chiave} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "8px", backgroundColor: "var(--cf-bg-subtle)", border: "1px solid var(--cf-border)" }}>
                        <span style={{ flex: "1 1 auto", fontSize: "13px", wordBreak: "break-word" }} title={g.etichetta}>
                          {g.etichetta} <span className="testo-secondario">({g.indici.length} riga/e)</span>
                        </span>
                        <span style={{ color: "var(--cf-text-faint)" }}>→</span>
                        <input
                          type="text"
                          placeholder="valore..."
                          value={perGruppo[g.chiave] || ""}
                          onChange={(e) => setPerGruppo((prev) => ({ ...prev, [g.chiave]: e.target.value }))}
                          style={{ minWidth: "10rem" }}
                        />
                      </div>
                    ))}
                  </div>
                  <button className="btn-primario" onClick={applicaPerGruppo} disabled={gruppiCompilati.length === 0}>
                    Applica ({gruppiCompilati.length}/{gruppi.length})
                  </button>
                </>
              )}
            </>
          )}
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--cf-border)", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <button onClick={onChiudi}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}
